import { signInWithEmailAndPassword, signInWithPopup, signInWithCredential, GoogleAuthProvider, setPersistence, browserLocalPersistence, browserSessionPersistence } from "firebase/auth";
import { FirebaseAuthentication } from "@capacitor-firebase/authentication";
import { getFunctions, httpsCallable } from "firebase/functions";
import { googleCancelado } from "./googleAuthErrors";

// Rotina compartilhada pelo painel Print Control e pelas lojas. A instância
// de Auth é recebida explicitamente: entrar na loja não troca a sessão do painel.
export const normalizarEmail = email => email.trim().toLowerCase();

export async function configurarSessao(auth, manterConectado) {
  if (typeof manterConectado === "boolean") {
    await setPersistence(auth, manterConectado ? browserLocalPersistence : browserSessionPersistence);
  }
}

export async function entrarComEmail(auth, email, senha, manterConectado) {
  await configurarSessao(auth, manterConectado);
  return signInWithEmailAndPassword(auth, normalizarEmail(email), senha);
}

export async function entrarComGoogle(auth, manterConectado) {
  await configurarSessao(auth, manterConectado);
  if (window.Capacitor?.getPlatform?.() === "android") {
    let resultado;
    try {
      resultado = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager:false, skipNativeAuth:true });
    } catch (error) {
      // O fluxo clássico pode falhar ao pedir accessToken/serverAuthCode,
      // que não usamos. Credential Manager sem scopes precisa só do ID token.
      // Não repetir cancelamento, erro de rede ou configuração do certificado.
      const retryable = /12500|BadAuthentication|GoogleAuthException|getToken|SIGN_IN_FAILED/i.test(error?.message || "");
      if(googleCancelado(error) || !retryable) throw error;
      resultado = await FirebaseAuthentication.signInWithGoogle({ useCredentialManager:true, skipNativeAuth:true, scopes:[] });
    }
    const idToken = resultado.credential?.idToken;
    if (!idToken) { const error = new Error("Não foi possível obter o token do Google."); error.code="auth/missing-google-token"; throw error; }
    return signInWithCredential(auth, GoogleAuthProvider.credential(idToken));
  }
  return signInWithPopup(auth, new GoogleAuthProvider());
}

// ── Confirmação do e-mail no login (painel e lojas) ─────────────
// Conta com e-mail ainda não confirmado; Google já chega confirmado. Mesma
// regra do servidor (functions/security/confirmacaoEmail.js), que também
// confere isso no checkout da loja e nos pagamentos.
export const precisaConfirmarEmail = user => !!user && !user.isAnonymous && !!user.email && user.emailVerified !== true;

// Traz o emailVerified novo da conta e renova o token (o servidor lê email_verified dele)
async function atualizarConfirmacao(auth) {
  const user = auth.currentUser;
  if (!user) return;
  await user.reload();
  await user.getIdToken(true);
}

/* Manda o código ao e-mail da conta logada. Devolve { confirmado } quando não
   precisa (já confirmado, ou recém-confirmado no cadastro) ou { enviado,
   email (mascarado), aguardarSegundos? }. */
export async function enviarCodigoConfirmacao(auth, { vendedorId } = {}) {
  const r = await httpsCallable(getFunctions(auth.app, "us-central1"), "enviarCodigoConfirmacaoEmail")(vendedorId ? { vendedorId } : {});
  if (r.data?.confirmado) await atualizarConfirmacao(auth);
  return r.data || {};
}

export async function confirmarCodigoEmail(auth, codigo) {
  await httpsCallable(getFunctions(auth.app, "us-central1"), "confirmarCodigoEmail")({ codigo: String(codigo || "").trim() });
  await atualizarConfirmacao(auth);
}

export function solicitarCodigoSenha(auth, email) {
  return httpsCallable(getFunctions(auth.app, "us-central1"), "solicitarResetSenha")({ email:normalizarEmail(email) });
}

export function confirmarCodigoSenha(auth, { email, code, novaSenha }) {
  return httpsCallable(getFunctions(auth.app, "us-central1"), "confirmarResetSenha")({ email:normalizarEmail(email), code:code.trim(), novaSenha });
}
